import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import useApi from '../hooks/useApi';
import { useAuth } from '../context/AuthContext';
import { useSettings } from '../context/SettingsContext';
import { Button } from '../components/ui/Button';
import { Loader2, CheckCircle, Clock, Inbox, Send, FolderOpen, Search, ExternalLink, FileText } from 'lucide-react';
import { format } from 'date-fns';

const Approvals = () => {
  const { user } = useAuth();
  const { get } = useApi();
  const navigate = useNavigate();
  const { settings, loading: settingsLoading } = useSettings();
  const [notingStatus, setNotingStatus] = useState(null);
  const [receiptApprovals, setReceiptApprovals] = useState([]);
  const [dispatchApprovals, setDispatchApprovals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const [search, setSearch] = useState('');

  const isEfileEnabled = settings?.efile_enabled == 1 || settings?.efile_enabled === 'true' || settings?.efile_enabled === true;
  const userId = user?.id;

  useEffect(() => {
    if (!userId) return;
    fetchApprovals();
  }, [userId, isEfileEnabled]);

  const fetchApprovals = async () => {
    setLoading(true);
    try {
      const [receiptsRes, dispatchRes, notingRes] = await Promise.all([
        get(`/receipt?page=1&limit=200&approvalStatus=Review&currentApproverId=${encodeURIComponent(userId)}`),
        get(`/dispatch?page=1&limit=200&approvalStatus=Review&currentApproverId=${encodeURIComponent(userId)}`),
        isEfileEnabled ? get(`/efile/notings/month-status`) : Promise.resolve({ data: null })
      ]);

      setReceiptApprovals(Array.isArray(receiptsRes?.data) ? receiptsRes.data : []);
      setDispatchApprovals(Array.isArray(dispatchRes?.data) ? dispatchRes.data : []);
      setNotingStatus(notingRes?.data || null);
    } catch (error) {
      console.error('Failed to fetch approvals', error);
      setReceiptApprovals([]);
      setDispatchApprovals([]);
      setNotingStatus(null);
    } finally {
      setLoading(false);
    }
  };

  const pendingNotings = useMemo(() => {
    if (!isEfileEnabled) return [];
    const items = Array.isArray(notingStatus?.items) ? notingStatus.items : [];
    return items.filter(it => it.status !== 'Approved');
  }, [isEfileEnabled, notingStatus]);

  const combinedItems = useMemo(() => {
    const receiptItems = (receiptApprovals || []).map(r => ({
      key: `receipt:${r.id}`,
      type: 'receipt',
      id: r.id,
      number: r.diaryNumber || r.receiptNumber || `Receipt #${r.id}`,
      title: r.subject || 'No Subject',
      subtitle: r.sender || '',
      priority: r.priority || '',
      status: r.approvalStatus || 'Review',
      date: r.updatedAt || r.createdAt || r.dateReceived || null
    }));

    const dispatchItems = (dispatchApprovals || []).map(d => ({
      key: `dispatch:${d.id}`,
      type: 'dispatch',
      id: d.id,
      number: d.dispatchNumber || `Dispatch #${d.id}`,
      title: d.subject || 'No Subject',
      subtitle: d.recipient || '',
      priority: d.priority || '',
      status: d.approvalStatus || 'Review',
      date: d.updatedAt || d.createdAt || d.dateIssued || null
    }));

    const notingItems = pendingNotings.map(n => ({
      key: `efile_noting:${n.notingId}`,
      type: 'efile_noting',
      id: n.notingId,
      fileId: n.fileId,
      number: n.fileNumber || 'File',
      title: n.fileSubject || 'No Subject',
      subtitle: n.notingTitle ? `Noting: ${n.notingTitle}` : '',
      priority: '',
      status: n.status || 'Pending',
      date: n.createdAt || null
    }));

    const all = [...receiptItems, ...dispatchItems, ...notingItems];
    all.sort((a, b) => {
      const ad = a.date ? new Date(a.date).getTime() : 0;
      const bd = b.date ? new Date(b.date).getTime() : 0;
      return bd - ad;
    });
    return all;
  }, [dispatchApprovals, pendingNotings, receiptApprovals]);

  const filteredItems = useMemo(() => {
    const q = String(search || '').trim().toLowerCase();
    return combinedItems.filter(it => {
      if (activeTab !== 'all' && it.type !== activeTab) return false;
      if (!q) return true;
      return (
        String(it.number || '').toLowerCase().includes(q) ||
        String(it.title || '').toLowerCase().includes(q) ||
        String(it.subtitle || '').toLowerCase().includes(q)
      );
    });
  }, [activeTab, combinedItems, search]);

  const loadDetail = async (item) => {
    if (!item) return;
    setSelected(item);
    setDetail(null);
    setDetailLoading(true);
    try {
      if (item.type === 'receipt') {
        const res = await get(`/receipt/${item.id}`);
        setDetail({ type: 'receipt', data: res.data });
      } else if (item.type === 'dispatch') {
        const res = await get(`/dispatch/${item.id}`);
        setDetail({ type: 'dispatch', data: res.data });
      } else if (item.type === 'efile_noting') {
        const res = await get(`/efile/${item.fileId}/workspace`, { validateStatus: () => true });
        if (!res?.success) {
          setDetail(null);
          return;
        }
        setDetail({ type: 'efile', data: res.data, selectedNotingId: item.id });
      }
    } catch (e) {
      console.error('Failed to load case detail', e);
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const tabs = useMemo(() => {
    const receiptCount = receiptApprovals.length;
    const dispatchCount = dispatchApprovals.length;
    const efileCount = pendingNotings.length;
    return [
      { key: 'all', label: 'All', count: receiptCount + dispatchCount + efileCount },
      { key: 'receipt', label: 'Receipts', count: receiptCount, icon: Inbox },
      { key: 'dispatch', label: 'Dispatches', count: dispatchCount, icon: Send },
      { key: 'efile_noting', label: 'e-File Notings', count: efileCount, icon: FolderOpen },
    ];
  }, [dispatchApprovals.length, pendingNotings.length, receiptApprovals.length]);

  return (
    <>
      <div className="flex flex-col gap-4 h-full min-h-0">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <h1 className="text-2xl font-bold text-gray-800">Pending Approvals</h1>
            <p className="text-gray-500">Receipts, dispatches, and e-File notings awaiting action.</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={fetchApprovals} icon={Clock} isLoading={loading}>
              Refresh
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
          <div className="p-4 rounded-lg border border-gray-200 bg-white">
            <div className="text-xs text-gray-500">Receipts</div>
            <div className="text-2xl font-bold text-gray-900">{receiptApprovals.length}</div>
            <div className="text-sm text-gray-600">Awaiting your review</div>
          </div>
          <div className="p-4 rounded-lg border border-gray-200 bg-white">
            <div className="text-xs text-gray-500">Dispatches</div>
            <div className="text-2xl font-bold text-gray-900">{dispatchApprovals.length}</div>
            <div className="text-sm text-gray-600">Awaiting your review</div>
          </div>
          <div className="p-4 rounded-lg border border-gray-200 bg-white">
            <div className="text-xs text-gray-500">e-File Notings</div>
            <div className="text-2xl font-bold text-gray-900">{pendingNotings.length}</div>
            <div className="text-sm text-gray-600">Pending status</div>
          </div>
          <div className="p-4 rounded-lg border border-gray-200 bg-white">
            <div className="text-xs text-gray-500">This Month</div>
            <div className="text-2xl font-bold text-gray-900">{notingStatus?.summary?.total || 0}</div>
            <div className="text-sm text-gray-600">Notings in your files</div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 flex-1 min-h-0">
          <div className="lg:col-span-5 flex flex-col min-h-0">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 flex flex-col min-h-0">
              <div className="p-4 border-b border-gray-100">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-2 flex-wrap">
                    {tabs.map(t => {
                      const Icon = t.icon;
                      const isActive = activeTab === t.key;
                      return (
                        <button
                          key={t.key}
                          type="button"
                          onClick={() => setActiveTab(t.key)}
                          className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-md border text-sm font-medium transition-colors ${
                            isActive ? 'bg-indigo-50 text-indigo-700 border-indigo-200' : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                          }`}
                        >
                          {Icon ? <Icon className="w-4 h-4" /> : null}
                          <span>{t.label}</span>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${isActive ? 'bg-indigo-100 text-indigo-800' : 'bg-gray-100 text-gray-700'}`}>
                            {t.count}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                  <div className="relative w-full sm:w-64">
                    <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                    <input
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="Search cases..."
                      className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-300"
                    />
                  </div>
                </div>
              </div>

              <div className="flex-1 min-h-0 overflow-y-auto">
                {(loading || settingsLoading) ? (
                  <div className="flex justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
                  </div>
                ) : filteredItems.length === 0 ? (
                  <div className="p-6">
                    <div className="text-center py-10 text-gray-500 bg-gray-50 rounded-lg border border-dashed border-gray-200">
                      <CheckCircle className="w-12 h-12 mx-auto text-green-500 mb-3" />
                      <h3 className="text-lg font-medium text-gray-900">No Pending Items</h3>
                      <p>No approvals match your current filters.</p>
                    </div>
                  </div>
                ) : (
                  <div className="p-3 space-y-2">
                    {filteredItems.map(item => {
                      const isActive = selected?.key === item.key;
                      const statusColor =
                        item.status === 'Approved'
                          ? 'text-green-700 bg-green-50 border-green-100'
                          : item.type === 'efile_noting'
                            ? 'text-amber-700 bg-amber-50 border-amber-100'
                            : 'text-indigo-700 bg-indigo-50 border-indigo-100';

                      const LeadingIcon = item.type === 'receipt' ? Inbox : item.type === 'dispatch' ? Send : FolderOpen;

                      return (
                        <button
                          key={item.key}
                          type="button"
                          onClick={() => loadDetail(item)}
                          className={`w-full text-left group p-4 bg-white border rounded-lg transition-all flex flex-col gap-2 ${
                            isActive ? 'border-indigo-300 ring-2 ring-indigo-100' : 'border-gray-200 hover:border-indigo-300 hover:shadow-sm'
                          }`}
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="inline-flex items-center gap-1 text-xs font-semibold text-gray-700 bg-gray-100 px-2 py-1 rounded">
                                  <LeadingIcon className="w-3.5 h-3.5" />
                                  {item.number}
                                </span>
                                <span className={`text-xs font-semibold px-2 py-1 rounded border ${statusColor}`}>
                                  {item.type === 'efile_noting' ? 'Pending' : 'Review'}
                                </span>
                                {item.date ? (
                                  <span className="text-xs text-gray-500 flex items-center gap-1">
                                    <Clock className="w-3 h-3" />
                                    {format(new Date(item.date), 'dd MMM yyyy HH:mm')}
                                  </span>
                                ) : null}
                              </div>
                              <div className="mt-1 text-sm font-medium text-gray-900 group-hover:text-indigo-700 line-clamp-2">
                                {item.title}
                              </div>
                              {item.subtitle ? (
                                <div className="text-xs text-gray-600 line-clamp-1">
                                  {item.subtitle}
                                </div>
                              ) : null}
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="lg:col-span-7 flex flex-col min-h-0">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 flex flex-col min-h-0">
              <div className="p-4 border-b border-gray-100 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-gray-900">Case Details</div>
                  <div className="text-xs text-gray-500">Click a case on the left to view full details.</div>
                </div>
                {selected ? (
                  <Button
                    variant="outline"
                    icon={ExternalLink}
                    onClick={() => {
                      if (selected.type === 'receipt') navigate(`/diary/${selected.id}/view`);
                      else if (selected.type === 'dispatch') navigate(`/dispatch/${selected.id}/view`);
                      else if (selected.type === 'efile_noting') navigate(`/efile/${selected.fileId}`);
                    }}
                  >
                    Open
                  </Button>
                ) : null}
              </div>

              <div className="flex-1 min-h-0 overflow-y-auto p-4">
                {detailLoading ? (
                  <div className="flex justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
                  </div>
                ) : !selected ? (
                  <div className="text-center py-12 text-gray-500 bg-gray-50 rounded-lg border border-dashed border-gray-200">
                    <FileText className="w-12 h-12 mx-auto text-indigo-500 mb-3" />
                    <h3 className="text-lg font-medium text-gray-900">Select a Case</h3>
                    <p>Full case information and complete noting appears here.</p>
                  </div>
                ) : !detail ? (
                  <div className="text-center py-12 text-gray-500 bg-gray-50 rounded-lg border border-dashed border-gray-200">
                    <CheckCircle className="w-12 h-12 mx-auto text-gray-400 mb-3" />
                    <h3 className="text-lg font-medium text-gray-900">Unable to load details</h3>
                    <p>Try refresh or open the case directly.</p>
                  </div>
                ) : detail.type === 'receipt' ? (
                  <div className="space-y-4">
                    <div>
                      <div className="text-xs text-gray-500">Diary No.</div>
                      <div className="text-lg font-semibold text-gray-900">{detail.data?.diaryNumber || detail.data?.receiptNumber}</div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <div className="text-xs text-gray-500">Sender</div>
                        <div className="text-sm text-gray-900">{detail.data?.sender || '-'}</div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500">Date Received</div>
                        <div className="text-sm text-gray-900">
                          {detail.data?.dateReceived ? format(new Date(detail.data.dateReceived), 'dd MMM yyyy') : '-'}
                        </div>
                      </div>
                      <div className="sm:col-span-2">
                        <div className="text-xs text-gray-500">Subject</div>
                        <div className="text-sm text-gray-900 whitespace-pre-wrap">{detail.data?.subject || '-'}</div>
                      </div>
                      <div className="sm:col-span-2">
                        <div className="text-xs text-gray-500">Remarks</div>
                        <div className="text-sm text-gray-900 whitespace-pre-wrap">{detail.data?.remarks || '-'}</div>
                      </div>
                    </div>

                    {(detail.data?.attachments?.length > 0) ? (
                      <div className="border border-gray-200 rounded-lg">
                        <div className="px-4 py-2 border-b border-gray-100 text-sm font-medium text-gray-800">Attachments</div>
                        <div className="p-3 space-y-2">
                          {detail.data.attachments.map(f => (
                            <div key={f.id} className="flex items-center justify-between gap-3 p-2 bg-gray-50 border border-gray-200 rounded-md">
                              <div className="min-w-0">
                                <div className="text-sm text-gray-800 truncate" title={f.originalName}>{f.originalName}</div>
                                <div className="text-xs text-gray-500 truncate">{f.mimeType}</div>
                              </div>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={async () => {
                                  const blob = await get(`/files/${f.fileName}`, { responseType: 'blob' });
                                  const url = window.URL.createObjectURL(new Blob([blob]));
                                  const link = document.createElement('a');
                                  link.href = url;
                                  link.setAttribute('download', f.originalName);
                                  document.body.appendChild(link);
                                  link.click();
                                  link.parentNode.removeChild(link);
                                }}
                              >
                                Download
                              </Button>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : null}
                  </div>
                ) : detail.type === 'dispatch' ? (
                  <div className="space-y-4">
                    <div>
                      <div className="text-xs text-gray-500">Dispatch No.</div>
                      <div className="text-lg font-semibold text-gray-900">{detail.data?.dispatchNumber}</div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <div className="text-xs text-gray-500">Recipient</div>
                        <div className="text-sm text-gray-900">{detail.data?.recipient || '-'}</div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500">Date Issued</div>
                        <div className="text-sm text-gray-900">
                          {detail.data?.dateIssued ? format(new Date(detail.data.dateIssued), 'dd MMM yyyy') : '-'}
                        </div>
                      </div>
                      <div className="sm:col-span-2">
                        <div className="text-xs text-gray-500">Subject</div>
                        <div className="text-sm text-gray-900 whitespace-pre-wrap">{detail.data?.subject || '-'}</div>
                      </div>
                      <div className="sm:col-span-2">
                        <div className="text-xs text-gray-500">Remarks</div>
                        <div className="text-sm text-gray-900 whitespace-pre-wrap">{detail.data?.remarks || '-'}</div>
                      </div>
                    </div>

                    {(detail.data?.attachments?.length > 0) ? (
                      <div className="border border-gray-200 rounded-lg">
                        <div className="px-4 py-2 border-b border-gray-100 text-sm font-medium text-gray-800">Attachments</div>
                        <div className="p-3 space-y-2">
                          {detail.data.attachments.map(f => (
                            <div key={f.id} className="flex items-center justify-between gap-3 p-2 bg-gray-50 border border-gray-200 rounded-md">
                              <div className="min-w-0">
                                <div className="text-sm text-gray-800 truncate" title={f.originalName}>{f.originalName}</div>
                                <div className="text-xs text-gray-500 truncate">{f.mimeType}</div>
                              </div>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={async () => {
                                  const blob = await get(`/files/${f.fileName}`, { responseType: 'blob' });
                                  const url = window.URL.createObjectURL(new Blob([blob]));
                                  const link = document.createElement('a');
                                  link.href = url;
                                  link.setAttribute('download', f.originalName);
                                  document.body.appendChild(link);
                                  link.click();
                                  link.parentNode.removeChild(link);
                                }}
                              >
                                Download
                              </Button>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : null}
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div>
                      <div className="text-xs text-gray-500">File No.</div>
                      <div className="text-lg font-semibold text-gray-900">{detail.data?.file?.fileNumber || selected.number}</div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500">Subject</div>
                      <div className="text-sm text-gray-900 whitespace-pre-wrap">{detail.data?.file?.subject || selected.title}</div>
                    </div>

                    {(Array.isArray(detail.data?.notings) && detail.data.notings.length > 0) ? (
                      <div className="border border-gray-200 rounded-lg overflow-hidden">
                        <div className="px-4 py-2 border-b border-gray-100 text-sm font-medium text-gray-800">Complete Noting</div>
                        <div className="divide-y divide-gray-100">
                          {detail.data.notings
                            .filter(n => Number(n.isDraft || 0) === 0)
                            .map(n => {
                              const isSelectedNoting = String(n.id) === String(detail.selectedNotingId);
                              return (
                                <div key={n.id} className={`p-4 ${isSelectedNoting ? 'bg-indigo-50' : 'bg-white'}`}>
                                  <div className="flex items-center justify-between gap-3 flex-wrap">
                                    <div className="text-sm font-medium text-gray-900">
                                      {n.subject || n.notingSubject || 'Noting'}
                                    </div>
                                    <div className="text-xs text-gray-500">
                                      {n.createdAt ? format(new Date(n.createdAt), 'dd MMM yyyy HH:mm') : ''}
                                    </div>
                                  </div>
                                  <div className="mt-2 text-sm text-gray-800 whitespace-pre-wrap">
                                    {n.remark || n.content || '-'}
                                  </div>
                                  <div className="mt-2 text-xs text-gray-500">
                                    {n.addedByName ? `By: ${n.addedByName}` : ''}
                                  </div>
                                </div>
                              );
                            })}
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-10 text-gray-500 bg-gray-50 rounded-lg border border-dashed border-gray-200">
                        <CheckCircle className="w-10 h-10 mx-auto text-gray-400 mb-2" />
                        <div className="text-sm font-medium text-gray-900">No notings found</div>
                        <div className="text-xs">Open the file workspace to add or review notings.</div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {!isEfileEnabled ? (
          <div className="text-center py-4 text-gray-500 bg-gray-50 rounded-lg border border-dashed border-gray-200">
            <div className="flex items-center justify-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <div className="text-sm">e-File is disabled, so e-File items are hidden.</div>
            </div>
          </div>
        ) : null}
      </div>
    </>
  );
};

export default Approvals;
